import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@shared/context/AuthContext";
import { UserProvider } from "./features/UserAccount/contexts/UserProvider";
import { GlobalStateProvider } from "./shared/context/GlobalStateContext";
import { FeatureFlagProvider } from "@shared/hooks/useFeatureFlags";
import { UnifiedNotificationsProvider } from "@shared/contexts/UnifiedNotificationsProvider";

// bootstrap.bundle.min.js removed: React Bootstrap reimplements all plugins
// in React and does not rely on Bootstrap's jQuery-based JS bundle.
import "bootstrap/dist/css/bootstrap.min.css";
import "@shared/styles/themes/medical-theme.scss";
import "@shared/styles/component-tokens.scss";
import "@shared/styles/typography.scss";
import "@shared/styles/styles.css";
import "@shared/styles/utilities.css";
import i18n from "./shared/i18n/config"; // REG-006: Initialize i18n for EU market access
import { getBuildTimeAppVersionInfo } from "@shared/config/appInfo";
import { bootstrapRuntimePublicConfig } from "@shared/config/runtimePublicConfig";
import logger from "@shared/utils/logger";

const queryClient = new QueryClient();
// Worklist Trans keys live in `features`. Defer `dashboard` until idle so the
// ~large namespace JSON is off the LCP critical path.
void i18n.loadNamespaces(["features"]).catch(() => undefined);

const queryDevtoolsFlag = String(
  import.meta.env.VITE_ENABLE_QUERY_DEVTOOLS || "",
).toLowerCase();
const isQueryDevtoolsEnabled =
  queryDevtoolsFlag === "1" || queryDevtoolsFlag === "true";
const buildTimeAppInfo = getBuildTimeAppVersionInfo();

const faroEndpoint = String(import.meta.env.VITE_FARO_ENDPOINT || "").trim();
const faroEnabled =
  String(import.meta.env.VITE_FARO_ENABLED || "").toLowerCase() === "true";

function redactTelemetryItem<T>(item: T): T {
  const serialized = JSON.stringify(item, (key, value) => {
    if (
      /password|token|secret|authorization|cookie|email|patient|mrn/i.test(key)
    ) {
      return "[REDACTED]";
    }
    if (typeof value !== "string") return value;
    return value
      .replace(
        /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi,
        "[REDACTED_EMAIL]",
      )
      .replace(
        /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi,
        "[REDACTED_ID]",
      )
      .replace(/Bearer\s+[A-Za-z0-9._~+/-]+=*/gi, "Bearer [REDACTED]");
  });
  return JSON.parse(serialized) as T;
}

function scheduleIdle(task: () => void, timeoutMs = 2500): void {
  if (typeof window === "undefined") return;
  const ric = (
    window as Window & {
      requestIdleCallback?: (
        cb: IdleRequestCallback,
        opts?: IdleRequestOptions,
      ) => number;
    }
  ).requestIdleCallback;
  if (typeof ric === "function") {
    ric(() => task(), { timeout: timeoutMs });
    return;
  }
  window.setTimeout(task, 1);
}

// Faro is useful but not LCP-critical — keep OTel/Faro off the boot graph.
scheduleIdle(() => {
  if (!faroEnabled || !faroEndpoint) return;
  void (async () => {
    const [{ initializeFaro }, { TracingInstrumentation }] = await Promise.all([
      import("@grafana/faro-web-sdk"),
      import("@grafana/faro-web-tracing"),
    ]);
    initializeFaro({
      url: faroEndpoint,
      app: {
        name: "serviceapi",
        version: buildTimeAppInfo.version ?? "dev",
        environment: import.meta.env.MODE,
      },
      beforeSend: (item) => redactTelemetryItem(item),
      ignoreUrls: [
        /\/api\/users\/login/i,
        /\/api\/users\/refresh-token/i,
        /\/api\/patients/i,
        /\/api\/break-glass/i,
      ],
      instrumentations: [new TracingInstrumentation()],
    });
  })().catch((error) => {
    logger.warn("[App] Faro init deferred failed", error);
  });
});

scheduleIdle(() => {
  void i18n.loadNamespaces(["dashboard"]).catch(() => undefined);
});

if (typeof window !== "undefined") {
  const runtimeInfo =
    ((window as any).__PLATFORM_RUNTIME_INFO__ as Record<string, unknown>) ||
    ((window as any).__NERVEDX_RUNTIME_INFO__ as Record<string, unknown>) ||
    {};
  runtimeInfo.serviceapi = {
    service: "serviceapi",
    ...buildTimeAppInfo,
    sourceShaShort: buildTimeAppInfo.sourceSha?.slice(0, 12) || null,
  };
  (window as any).__PLATFORM_RUNTIME_INFO__ = runtimeInfo;
}

// Keep the devtools bundle cold unless it is explicitly enabled.
const QueryDevtoolsPanel = React.lazy(() =>
  import("@tanstack/react-query-devtools").then((module) => ({
    default: module.ReactQueryDevtools,
  })),
);

/** Chat widgets are not LCP-critical — mount after first paint / idle. */
function DeferredSupportChatProviders({
  children,
}: {
  children: React.ReactNode;
}) {
  const [ChatTree, setChatTree] = React.useState<React.ComponentType<{
    children: React.ReactNode;
  }> | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    scheduleIdle(() => {
      void Promise.all([
        import("@shared/context/FreshchatContext"),
        import("@shared/context/ChatwootContext"),
      ])
        .then(([fresh, chatwoot]) => {
          if (cancelled) return;
          const FreshchatProvider = fresh.FreshchatProvider;
          const ChatwootProvider = chatwoot.ChatwootProvider;
          setChatTree(() =>
            function SupportChatProviders({
              children: inner,
            }: {
              children: React.ReactNode;
            }) {
              return (
                <FreshchatProvider>
                  <ChatwootProvider>{inner}</ChatwootProvider>
                </FreshchatProvider>
              );
            },
          );
        })
        .catch((error) => {
          logger.warn("[App] Support chat providers deferred load failed", error);
        });
    }, 4000);
    return () => {
      cancelled = true;
    };
  }, []);

  if (!ChatTree) {
    return <>{children}</>;
  }
  return <ChatTree>{children}</ChatTree>;
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

const root = ReactDOM.createRoot(rootElement as HTMLElement);
void bootstrapRuntimePublicConfig().finally(() => {
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <GlobalStateProvider>
            {" "}
            {/* Wrap with GlobalStateProvider */}
            <UserProvider>
              <AuthProvider>
                <FeatureFlagProvider>
                  <UnifiedNotificationsProvider>
                    <DeferredSupportChatProviders>
                      <App />
                    </DeferredSupportChatProviders>
                  </UnifiedNotificationsProvider>
                </FeatureFlagProvider>
              </AuthProvider>
            </UserProvider>
          </GlobalStateProvider>
        </BrowserRouter>
        {isQueryDevtoolsEnabled ? (
          <React.Suspense fallback={null}>
            <QueryDevtoolsPanel initialIsOpen={false} />
          </React.Suspense>
        ) : null}
      </QueryClientProvider>
    </React.StrictMode>,
  );
});

// Register Service Worker for Plotly bundle caching (Priority 3 optimization)
// Only in production to avoid caching issues during development
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((registration) => {
        logger.info(
          "[App] Service Worker registered successfully:",
          registration.scope,
        );

        // Listen for updates
        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener("statechange", () => {
              if (
                newWorker.state === "installed" &&
                navigator.serviceWorker.controller
              ) {
                logger.info(
                  "[App] New Service Worker available - reload to update",
                );
              }
            });
          }
        });
      })
      .catch((error) => {
        logger.error("[App] Service Worker registration failed:", error);
      });
  });
}
